import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Boxes,
  Tag,
  Wine,
  Thermometer,
  Scissors,
  Hand,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile à Noisy-le-Roi ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Votre livraison de cartons à domicile {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est précieux et que transporter des
            fournitures volumineuses n&apos;est pas votre métier, nous
            livrons votre commande à plat directement sur le pas de votre
            porte.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Proximité immédiate",
              body: "Notre service logistique dessert quotidiennement Noisy-le-Roi ainsi que les communes voisines comme Bailly, Saint-Nom-la-Bretèche, Rennemoulin, Fontenay-le-Fleury et Villepreux.",
              Icon: Truck,
            },
            {
              title: "Créneau horaire sur-mesure",
              body: "Nous fixons ensemble un rendez-vous précis et respecté pour s'intégrer parfaitement dans votre emploi du temps sans vous faire attendre.",
              Icon: Calendar,
            },
            {
              title: "Protection des produits",
              body: "L'intégralité de vos cartons et accessoires vous est livrée sous film hermétique, propre et sèche, prête pour un pliage immédiat.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Protocole Grands Crus ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Protocole &laquo;&nbsp;Grands Crus&nbsp;&raquo;
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Sécurisez votre cave et vos bouteilles
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              {city.name} abrite de belles caves de passionnés et de
              collectionneurs. Transporter du verre et des liquides
              précieux ne s&apos;improvise pas sous peine de gâcher des
              années de vieillissement.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "L'isolation individuelle antichoc",
                  body: "N'alignez jamais des bouteilles de vin ou de spiritueux côte à côte sans protection dans un carton classique. Nous mettons à votre disposition nos cartons bouteilles spécifiques avec calages renforcés. Ces modules maintiennent chaque bouteille debout et isolée de sa voisine par une double épaisseur de carton ondulé.",
                  Icon: Wine,
                },
                {
                  title: "Température et manutention",
                  body: "Pour vos grands crus, évitez les chocs thermiques brutaux juste avant le transport. Une fois vos bouteilles installées dans leurs cartons dédiés, scellez le dessus avec notre adhésif et transportez le carton avec la mention « Haut / Bas » bien visible. À l'arrivée, laissez vos bouteilles reposer quelques semaines en cave avant de les déboucher.",
                  Icon: Thermometer,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-demenagement?categorie=bouteilles"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-noisy-le-roi-cta-bouteilles"
            >
              <Wine className="h-4 w-4" strokeWidth={2.6} />
              Voir nos cartons bouteilles
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Wine className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Cartons cave dédiés
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Une protection à la hauteur de votre cave : alvéoles
              renforcées, double épaisseur de carton ondulé entre chaque
              bouteille.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                12 bouteilles, 75 verres ou + au choix
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Calage carton ondulé renforcé
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Stabilité verticale parfaite
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Marquage « Haut / Bas »
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Accessoires de fermeture et manutention ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Fermeture &amp; manutention
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Bloquez et déplacez vos cartons comme un pro
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un matériel haut de gamme demande des outils adaptés pour
            sécuriser la fermeture et faciliter le transport des charges
            sans effort physique.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Le ruban adhésif technique",
              body: "Conçu spécifiquement pour le carton ondulé, notre adhésif ne se décolle pas avec les changements de température ou l'humidité. Appliquez la méthode du « H » (une bande centrale et deux bandes sur les rabats latéraux) pour un scellage hermétique et inviolable.",
              Icon: Tag,
            },
            {
              title: "Le dévidoir pistolet professionnel",
              body: "L'outil indispensable pour gagner un temps fou. Équipé d'une lame crantée sécurisée et d'un frein réglable, il vous permet de tendre et couper le scotch d'une seule main, sans jamais chercher le début du rouleau.",
              Icon: Scissors,
            },
            {
              title: "Le diable et le chariot à roulettes",
              body: "Indispensables pour préserver votre dos le jour J. Notre diable professionnel permet d'empiler et de déplacer jusqu'à 4 cartons fermés en un seul voyage. Nos chariots plateaux se glissent sous vos meubles lourds ou l'électroménager pour les faire rouler sans aucun effort du logement jusqu'au camion.",
              Icon: Hand,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-noisy-le-roi-cta-fermeture"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Voir tous nos accessoires
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-noisy-le-roi-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-noisy-le-roi-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
